import {Checkbox, Form, FormInstance, InputNumber, Switch, TimePicker} from 'antd'
import {SecondaryTherapyDataSchemaElement} from 'src/constants/secondaryTherapyFields'
import {FREQUENCY_UNIT_OPTIONS, TOLERANCE_DAYS_UNIT_OPTIONS, WEEKDAY_OPTIONS} from './therapyDialogsConstants'
import {TwoColumnsGrid} from './TherapyGridStyled'
import UnitSelector from './UnitSelector'

const getContainer = () => document.body

const requiredUnitValidator = (_: any, value: any) => {
  if (value?.value !== undefined && value?.value !== null && value?.unitType) {
    return Promise.resolve()
  }
  return Promise.reject(new Error('Campo obbligatorio'))
}

type FrequencyFieldsProps = {
  form: FormInstance
  dataSchema: SecondaryTherapyDataSchemaElement[]
}

const FrequencyFields: React.FC<FrequencyFieldsProps> = ({form, dataSchema}) => {
  const asNeeded = Form.useWatch(['frequency', 'asNeeded'], form)
  const each = Form.useWatch(['frequency', 'each'], form)
  const times = Form.useWatch(['frequency', 'times'], form)
  const days: string[] = Form.useWatch(['frequency', 'days'], form) ?? []
  const isDaysSelectorVisible = each?.unitType === 'WEEK'
  // "Ripeti nei giorni" can't have more days checked than "Numero assunzioni": you can't take the
  // drug more days in a week than the number of times you take it that week.
  const maxDays: number | undefined = isDaysSelectorVisible ? times : undefined

  const dayOptions = WEEKDAY_OPTIONS.map((opt) => ({
    ...opt,
    disabled: !isDaysSelectorVisible || (maxDays !== undefined && !days.includes(opt.value) && days.length >= maxDays),
  }))

  const isFieldVisible = (key: string): boolean => dataSchema.some((element) => element.value === key)
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback
  // "Ogni" non ha una voce propria nel catalogo: è il complemento di "Numero assunzioni" (esprime
  // "N volte ogni M giorni/settimane/mesi"), quindi segue la sua stessa visibilità.
  const isTimesVisible = isFieldVisible('frequency.times')

  return (
    <div>
      <TwoColumnsGrid>
        {isFieldVisible('frequency.asNeeded') && (
          <Form.Item label={fieldLabel('frequency.asNeeded', 'Al bisogno')} name={['frequency', 'asNeeded']} valuePropName='checked'>
            <Switch />
          </Form.Item>
        )}
      </TwoColumnsGrid>
      {!asNeeded && (
        <TwoColumnsGrid>
          {isTimesVisible && (
            <Form.Item
              label={fieldLabel('frequency.times', 'Numero assunzioni')}
              name={['frequency', 'times']}
              rules={[{required: true, message: 'Campo obbligatorio'}]}
            >
              <InputNumber
                min={1}
                style={{width: '100%'}}
                onChange={(value) => {
                  // Lowering the number of intakes below the already-checked day count trims the
                  // extra days rather than leaving a now-invalid selection in place.
                  if (isDaysSelectorVisible && typeof value === 'number' && days.length > value) {
                    form.setFieldValue(['frequency', 'days'], days.slice(0, value))
                  }
                }}
              />
            </Form.Item>
          )}
          {isTimesVisible && (
            <Form.Item label='Ogni' name={['frequency', 'each']} rules={[{validator: requiredUnitValidator}]}>
              <UnitSelector unitTypeOptions={FREQUENCY_UNIT_OPTIONS} />
            </Form.Item>
          )}
          {isFieldVisible('frequency.days') && (
            <Form.Item label={fieldLabel('frequency.days', 'Ripeti nei giorni')} name={['frequency', 'days']}>
              <Checkbox.Group options={dayOptions} />
            </Form.Item>
          )}
          {isFieldVisible('frequency.notificationTime') && (
            <Form.Item label={fieldLabel('frequency.notificationTime', 'Orario')} name={['frequency', 'notificationTime']}>
              <TimePicker format='HH:mm' getPopupContainer={getContainer} popupStyle={{zIndex: 1400}} style={{width: '100%'}} />
            </Form.Item>
          )}
          {isFieldVisible('frequency.toleranceDays') && (
            <Form.Item label={fieldLabel('frequency.toleranceDays', 'Giorni di tolleranza')} name={['frequency', 'toleranceDays']}>
              <UnitSelector unitTypeOptions={TOLERANCE_DAYS_UNIT_OPTIONS} />
            </Form.Item>
          )}
        </TwoColumnsGrid>
      )}
    </div>
  )
}

export default FrequencyFields
